import { Navigate, type RouteObject } from "react-router";
import { RedirectIfAuthenticated, RequireAuth, RequireSession, isSuperAdmin } from "@/app/guards";
import { NotFoundRoute } from "@/app/not-found-route";
import type { RouteHandle } from "@/app/route-handle";
import { RootLayout } from "@/app/root-layout";
import { RouteError } from "@/app/route-error";
import { PlatformShell } from "@/components/layout/platform-shell";

/** The route tree served on the platform address (admin.<root domain>). */
export const platformRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: "update-password",
        lazy: () =>
          import("@/features/auth/routes/update-password-route").then((m) => ({ Component: m.UpdatePasswordRoute })),
      },
      {
        element: <RedirectIfAuthenticated />,
        children: [
          {
            path: "sign-in",
            lazy: () => import("@/features/auth/routes/sign-in-route").then((m) => ({ Component: m.SignInRoute })),
          },
          {
            path: "password-reset",
            lazy: () =>
              import("@/features/auth/routes/password-reset-route").then((m) => ({ Component: m.PasswordResetRoute })),
          },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <PlatformShell />,
            children: [
              // signing in lands on /home everywhere; here that is the dashboard
              { path: "home", element: <Navigate to="/" replace /> },
              {
                element: <RequireSession allow={isSuperAdmin} />,
                children: [
                  {
                    index: true,
                    handle: { crumbs: [{ label: "Dashboard" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/dashboard-route").then((m) => ({ Component: m.DashboardRoute })),
                  },
                  {
                    path: "tenants",
                    handle: { crumbs: [{ label: "Organizations" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/tenants-route").then((m) => ({ Component: m.TenantsRoute })),
                  },
                  {
                    path: "tenants/new",
                    handle: {
                      crumbs: [{ label: "Organizations", href: "/tenants" }, { label: "New organization" }],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/tenant-new-route").then((m) => ({ Component: m.TenantNewRoute })),
                  },
                  {
                    path: "tenants/:tenantId",
                    handle: {
                      crumbs: [{ label: "Organizations", href: "/tenants" }, { label: "Organization" }],
                    } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/tenant-detail-route").then((m) => ({
                        Component: m.TenantDetailRoute,
                      })),
                  },
                  {
                    path: "card-usage",
                    handle: { crumbs: [{ label: "Card usage" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/card-usage-route").then((m) => ({
                        Component: m.CardUsageRoute,
                      })),
                  },
                  {
                    path: "sync-log",
                    handle: { crumbs: [{ label: "Sync log" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/sync-log-route").then((m) => ({ Component: m.SyncLogRoute })),
                  },
                  {
                    path: "audit",
                    handle: { crumbs: [{ label: "Audit log" }] } satisfies RouteHandle,
                    lazy: () =>
                      import("@/features/platform/routes/audit-route").then((m) => ({ Component: m.AuditRoute })),
                  },
                ],
              },
              { path: "*", element: <NotFoundRoute /> },
            ],
          },
        ],
      },
    ],
  },
];
